import {
  createContext,
  PropsWithChildren,
  useState,
  useEffect,
  useMemo,
  useContext,
  SetStateAction,
  Dispatch,
} from 'react';
import { Questionnaire } from '@/typings/Questionnaire';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';

type FormEditorContextValues = {
  formData: Questionnaire.Questionnaire;
  fieldsByKey: Record<string, Questionnaire.Field>;
  fieldsById: Record<string, Questionnaire.Field>;
  setFormData: Dispatch<SetStateAction<Questionnaire.Questionnaire | undefined>>;
  selectedFieldEditorTab: 'settings' | 'conditions' | 'autofill' | 'assignees' | 'AI' | 'pdf-fill';
  setSelectedFieldEditorTab: Dispatch<SetStateAction<'settings' | 'conditions' | 'autofill' | 'assignees' | 'AI' | 'pdf-fill'>>;
  pdfTemplates: Awaited<ReturnType<typeof tsClient.pdfTemplates.getPdfTemplates>>['items'];
};

const FormEditorContext = createContext<FormEditorContextValues>(undefined as never);

type FormEditorProviderProps = {
  creatorId: string;
  formData: Questionnaire.Questionnaire;
  fieldsByKey: Record<string, Questionnaire.Field>;
  updateTemplate: Dispatch<SetStateAction<Questionnaire.Questionnaire | undefined>>;
};

const FormEditorProvider: React.FC<PropsWithChildren<FormEditorProviderProps>> = ({
  creatorId,
  formData: initFormData,
  fieldsByKey,
  children,
  updateTemplate,
}) => {
  const [formData, setFormData] = useState<Questionnaire.Questionnaire>(initFormData);
  const [selectedFieldEditorTab, setSelectedFieldEditorTab] = useState<'settings' | 'conditions' | 'autofill' | 'assignees' | 'AI' | 'pdf-fill'>('settings');

  const { data: pdfTemplates, getData: getPdfTemplates } = useNoodleApi(tsClient.pdfTemplates.getPdfTemplates);

  useEffect(() => setFormData(initFormData), [initFormData]);

  useEffect(() => {
    getPdfTemplates({ creatorId, page: 1, perPage: 100_000 });
  }, [getPdfTemplates, creatorId]);

  const fieldsById: Record<string, Questionnaire.Field> = useMemo(() => Object.values(fieldsByKey).reduce((acc, val) => ({
    ...acc,
    [val.id]: val,
  }), {}), [fieldsByKey]);

  const values = useMemo(() => ({
    fieldsById,
    fieldsByKey,
    formData,
    pdfTemplates: pdfTemplates?.items ?? [],
    selectedFieldEditorTab,
    setFormData: updateTemplate,
    setSelectedFieldEditorTab,
  }), [formData, selectedFieldEditorTab, fieldsByKey, pdfTemplates, updateTemplate, fieldsById]);

  return (
    <FormEditorContext.Provider value={values}>
      {children}
    </FormEditorContext.Provider>
  );
};

export const useFormEditorContext = (): FormEditorContextValues => useContext(FormEditorContext);
export default FormEditorProvider;
